// Stable identity for a media payload item (attachment/blob ids, falling back
// to ids embedded in the media URL). The media dock keys tracks off these
// identities, so every producer of dock media keys — inline payload players
// and the radio alike — must derive them through this module.

const BLOB_REDIRECT_PATH_RE = /\/api\/v1\/blob\/([^/?#]+)/

export type PayloadMediaIdentitySource = {
    url?: string | null
    attachmentId?: string | null
    blobId?: string | null
    mediaIdentityKey?: string | null
}

export function blobIdFromPayloadUrl(url: string | null | undefined): string | null {
    if (!url) return null

    const match = url.match(BLOB_REDIRECT_PATH_RE)
    return match?.[1] || null
}

export function payloadUrlSearchParam(url: string | null | undefined, name: string): string | null {
    if (!url) return null

    try {
        return new URL(url, 'http://payload.local').searchParams.get(name)
    } catch {
        return null
    }
}

export function payloadMediaStableIdentityKey(item: PayloadMediaIdentitySource): string | null {
    const attachmentId = item.attachmentId || payloadUrlSearchParam(item.url, 'attachment_id')
    const blobId =
        item.blobId || payloadUrlSearchParam(item.url, 'blob_id') || blobIdFromPayloadUrl(item.url)

    if (attachmentId && blobId) {
        return `attachment:${String(attachmentId)}:blob:${String(blobId)}`
    }
    if (blobId) return `blob:${String(blobId)}`
    if (attachmentId) return `attachment:${String(attachmentId)}`
    if (item.mediaIdentityKey) return `media:${String(item.mediaIdentityKey)}`

    return null
}

export function payloadMediaIdentityKey(item: PayloadMediaIdentitySource): string | null {
    return payloadMediaStableIdentityKey(item) || item.url || null
}

export function audioAttachmentMediaKey(item: PayloadMediaIdentitySource): string | null {
    const identityKey = payloadMediaIdentityKey(item)
    return identityKey ? `audio-attachment:${identityKey}` : null
}

export function videoPayloadMediaKey(item: PayloadMediaIdentitySource): string | null {
    const identityKey = payloadMediaIdentityKey(item)
    return identityKey ? `payload-video:${identityKey}` : null
}
